import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { ChevronDown, UserX } from "lucide-react";
import {
  useTablemates,
  usePostDinnerFeedback,
  useProfile,
  useUpdateConnections,
  useNoShows,
  useReportNoShow,
} from "@/lib/api";
import { useToast } from "@/components/ux/Toast";
import { ContactMethodToggles } from "./ContactMethodToggles";
import { useT } from "@/lib/i18n";
import { ProfileImage } from "@/components/ProfileImage";
import { PublicProfileModal } from "@/components/PublicProfileModal";

interface ConnectionsEditorProps {
  bookingId: string;
}

/**
 * Post-dinner sharing editor — renders the tablemate roster with a per-row
 * "Connect" button that opens a fixed-position dropdown containing the contact
 * method toggle chips and a "no-show" action.
 *
 * Self-contained: only needs the bookingId. Manages all its own data fetching
 * and debounced saves internally.
 */
export function ConnectionsEditor({ bookingId }: ConnectionsEditorProps) {
  const { t } = useT();
  const toast = useToast();
  const [, navigate] = useLocation();

  const goToConnections = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const target = document.getElementById("my-connections");
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    navigate("/connections");
    requestAnimationFrame(() => {
      document
        .getElementById("my-connections")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };
  const { data: tablemates = [], isLoading: tablematesLoading } =
    useTablemates(bookingId);
  const { data: existingFeedback } = usePostDinnerFeedback(bookingId);
  const { data: profileData } = useProfile();
  const { data: noShowList = [] } = useNoShows(bookingId);
  const updateConnections = useUpdateConnections();
  const reportNoShow = useReportNoShow();

  const [selectedByTablemate, setSelectedByTablemate] = useState<
    Record<string, string[]>
  >({});
  const [savingUsers, setSavingUsers] = useState<Set<string>>(new Set());
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  // The tablemate whose public profile is open in the modal (null = closed).
  const [profileUserId, setProfileUserId] = useState<string | null>(null);
  const [menuCoords, setMenuCoords] = useState<{ top: number; right: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const pendingUpdatesRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  );
  const reportedNoShows = new Set(noShowList);

  const openMenuFor = (tablemateId: string) => {
    const btn = buttonRefs.current.get(tablemateId);
    if (btn) {
      const rect = btn.getBoundingClientRect();
      setMenuCoords({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
    setOpenMenuId(tablemateId);
  };

  useEffect(() => {
    if (!openMenuId) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const btn = buttonRefs.current.get(openMenuId);
      if (menuRef.current?.contains(target)) return;
      if (btn?.contains(target)) return;
      setOpenMenuId(null);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, [openMenuId]);

  const userEmail = profileData?.data?.user?.email;
  const userPhone = profileData?.data?.user?.phone;
  const userInstagram = profileData?.data?.user?.instagram;
  const allAvailableMethods = ["email", "phone", "instagram"].filter((m) => {
    if (m === "email") return !!userEmail;
    if (m === "phone") return !!userPhone;
    if (m === "instagram") return !!userInstagram;
    return false;
  });

  useEffect(() => {
    if (existingFeedback?.connections) {
      const methodsByTablemate: Record<string, string[]> = {};
      existingFeedback.connections.forEach(
        (conn: { toUserId: string; contactMethod: string }) => {
          if (!methodsByTablemate[conn.toUserId]) {
            methodsByTablemate[conn.toUserId] = [];
          }
          if (!methodsByTablemate[conn.toUserId].includes(conn.contactMethod)) {
            methodsByTablemate[conn.toUserId].push(conn.contactMethod);
          }
        },
      );
      setSelectedByTablemate(methodsByTablemate);
    }
  }, [existingFeedback]);

  useEffect(() => {
    const pending = pendingUpdatesRef.current;
    return () => {
      pending.forEach((timeout) => clearTimeout(timeout));
    };
  }, []);

  const saveConnectionForUser = (tablemateId: string, methods: string[]) => {
    const existing = pendingUpdatesRef.current.get(tablemateId);
    if (existing) clearTimeout(existing);

    setSavingUsers((prev) => new Set(prev).add(tablemateId));

    const timeout = setTimeout(() => {
      pendingUpdatesRef.current.delete(tablemateId);
      updateConnections.mutate(
        {
          bookingId,
          connections: [
            {
              toUserId: tablemateId,
              contactMethods: methods as ("email" | "phone" | "instagram")[],
            },
          ],
        },
        {
          onSuccess: () => {
            setSavingUsers((prev) => {
              const next = new Set(prev);
              next.delete(tablemateId);
              return next;
            });
          },
          onError: (error: any) => {
            setSavingUsers((prev) => {
              const next = new Set(prev);
              next.delete(tablemateId);
              return next;
            });
            toast.error(
              error?.response?.data?.error || t("reservation.failedToSaveConnections"),
            );
          },
        },
      );
    }, 300);

    pendingUpdatesRef.current.set(tablemateId, timeout);
  };

  const toggleContactMethod = (tablemateId: string, method: string) => {
    const current = selectedByTablemate[tablemateId] || [];
    const next = current.includes(method)
      ? current.filter((m) => m !== method)
      : [...current, method];

    if (next.length === 0) {
      setSelectedByTablemate((prev) => {
        const copy = { ...prev };
        delete copy[tablemateId];
        return copy;
      });
    } else {
      setSelectedByTablemate((prev) => ({ ...prev, [tablemateId]: next }));
    }

    saveConnectionForUser(tablemateId, next);
  };

  const handleReportNoShow = (tablemateId: string) => {
    const alreadyReported = reportedNoShows.has(tablemateId);
    reportNoShow.mutate(
      { bookingId, reportedUserId: tablemateId },
      {
        onSuccess: () => {
          if (alreadyReported) {
            toast.success(t("reservation.noShowReportRemoved"));
          } else {
            setSelectedByTablemate((prev) => {
              const copy = { ...prev };
              delete copy[tablemateId];
              return copy;
            });
            saveConnectionForUser(tablemateId, []);
            toast.success(t("reservation.noShowReported"));
          }
        },
        onError: (error: any) => {
          toast.error(error?.message || t("reservation.failedToUpdateNoShow"));
        },
      },
    );
  };

  return (
    <div className="space-y-3">
      {!tablematesLoading && tablemates.length > 0 && (
        <>
          <div className="space-y-3">
            {tablemates.map((tablemate) => {
              const selectedMethods =
                selectedByTablemate[tablemate.id] || [];
              const isNoShow = reportedNoShows.has(tablemate.id);
              const isMenuOpen = openMenuId === tablemate.id;
              const isSharing = selectedMethods.length > 0;

              return (
                <div
                  key={tablemate.id}
                  className="flex items-center justify-between gap-3 select-none"
                >
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setProfileUserId(tablemate.id);
                    }}
                    aria-label={`View ${tablemate.firstName}'s profile`}
                    className="flex items-center gap-3 min-w-0 flex-1 text-left group"
                  >
                    <ProfileImage
                      user={tablemate}
                      size="xs"
                      className={isNoShow ? "opacity-50 grayscale" : ""}
                    />
                    <div className="min-w-0">
                      <div
                        className={`font-medium truncate group-hover:text-primary-600 transition-colors ${isNoShow ? "text-dark-muted line-through" : ""}`}
                      >
                        {tablemate.firstName}
                        {tablemate.lastName ? ` ${tablemate.lastName}` : ""}
                      </div>
                      {isNoShow && (
                        <div className="text-xs text-red-500">
                          {t("reservation.reportedNoShow")}
                        </div>
                      )}
                    </div>
                  </button>

                  <button
                    ref={(el) => {
                      if (el) buttonRefs.current.set(tablemate.id, el);
                      else buttonRefs.current.delete(tablemate.id);
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isMenuOpen) {
                        setOpenMenuId(null);
                      } else {
                        openMenuFor(tablemate.id);
                      }
                    }}
                    className={`btn-inline shrink-0 ${
                      isNoShow
                        ? "!bg-red-500/15 !text-red-600 dark:!text-red-400 hover:!bg-red-500/25"
                        : isSharing
                          ? "!bg-primary-500/15 !text-primary-600 dark:!text-primary-300 hover:!bg-primary-500/25"
                          : ""
                    }`}
                  >
                    {isNoShow
                      ? t("reservation.noShow")
                      : isSharing
                        ? t("reservation.sharingCount", { count: selectedMethods.length })
                        : t("reservation.connect")}
                    <ChevronDown
                      className={`w-3.5 h-3.5 transition-transform ${isMenuOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-dark-muted">
            {t("reservation.connectionsDisclaimerPrefix")}
            <a
              href="/connections#my-connections"
              className="text-primary-600 dark:text-primary-400 hover:text-primary-500 dark:hover:text-primary-300 font-medium transition-colors"
              onClick={goToConnections}
            >
              {t("reservation.connectionsDisclaimerLink")}
            </a>
            {t("reservation.connectionsDisclaimerSuffix")}
          </p>
        </>
      )}

      {openMenuId &&
        menuCoords &&
        (() => {
          const selectedMethods = selectedByTablemate[openMenuId] || [];
          const isNoShow = reportedNoShows.has(openMenuId);
          const isSaving = savingUsers.has(openMenuId);
          return (
            <div
              ref={menuRef}
              className="fixed w-64 rounded-2xl border border-content bg-white/95 dark:bg-neutral-900/95 backdrop-blur-xl shadow-[0_8px_24px_0_rgba(0,0,0,0.25)] z-50 overflow-hidden"
              style={{
                top: menuCoords.top,
                right: Math.max(menuCoords.right, 8),
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {!isNoShow && (
                <div className="px-3 py-3">
                  <p className="text-xs text-dark-muted mb-2 font-medium">
                    {t("reservation.shareContactInfo")}
                  </p>
                  {allAvailableMethods.length > 0 ? (
                    <ContactMethodToggles
                      availableMethods={allAvailableMethods}
                      selectedMethods={selectedMethods}
                      onToggle={(method) =>
                        toggleContactMethod(openMenuId, method)
                      }
                      disabled={isNoShow}
                      saving={isSaving}
                    />
                  ) : (
                    <div className="text-xs text-dark-muted italic">
                      {t("reservation.addContactInfoInProfile")}
                    </div>
                  )}
                </div>
              )}
              {!isNoShow && (
                <div className="border-t border-neutral-400/15 dark:border-neutral-500/15" />
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleReportNoShow(openMenuId);
                  setOpenMenuId(null);
                }}
                className={`w-full px-4 py-3 text-left text-sm flex items-center gap-2.5 hover:bg-black/5 dark:hover:bg-white/5 transition-colors ${
                  isNoShow
                    ? "text-red-600 dark:text-red-400"
                    : "text-neutral-700 dark:text-neutral-200 hover:text-red-600 dark:hover:text-red-400"
                }`}
              >
                <UserX className="w-4 h-4 flex-shrink-0" />
                {isNoShow
                  ? t("reservation.undoNoShowReport")
                  : t("reservation.reportNoShow")}
              </button>
            </div>
          );
        })()}

      <PublicProfileModal
        userId={profileUserId}
        isOpen={!!profileUserId}
        onClose={() => setProfileUserId(null)}
      />
    </div>
  );
}
